/**
 * Snapping for plan and drawing editors: to a grid, to angle steps, to
 * existing points and onto segments. Pure functions in world units. Point and
 * segment snapping take their tolerance in screen pixels plus the current
 * `pixelsPerUnit` (from `NxPlanCanvas`), so the catch radius feels the same
 * at every zoom level.
 */
import {
  normalizeAngle,
  pointAtAngle,
  pointDistance,
  projectOntoSegment,
  segmentAngle,
  type NxPlanPoint,
} from './geometry';

export interface NxPlanSnapTolerance {
  /** Catch radius in screen pixels. */
  tolerancePx: number;
  /** Current scale: screen pixels per world unit. */
  pixelsPerUnit: number;
}

/** A screen-pixel distance in world units at the given scale. */
export function screenToWorldDistance(px: number, pixelsPerUnit: number): number {
  return pixelsPerUnit > 0 ? px / pixelsPerUnit : 0;
}

/** Rounds `value` to the nearest multiple of `step` (offset by `origin`); `step` ≤ 0 = unchanged. */
export function snapValue(value: number, step: number, origin = 0): number {
  if (!(step > 0)) return value;
  const snapped = Math.round((value - origin) / step) * step + origin;
  // Keep float residue (0.30000000000000004) off the grid values.
  return Number(snapped.toFixed(10));
}

/** Snaps a point to the nearest grid intersection (`step` in world units). */
export function snapToGrid(
  point: NxPlanPoint,
  step: number,
  origin: NxPlanPoint = { x: 0, y: 0 }
): NxPlanPoint {
  return { x: snapValue(point.x, step, origin.x), y: snapValue(point.y, step, origin.y) };
}

/** Rounds an angle in degrees to the nearest multiple of `step` (e.g. 15°), in `[0, 360)`. */
export function snapAngle(degrees: number, step: number): number {
  if (!(step > 0)) return normalizeAngle(degrees);
  return normalizeAngle(Math.round(degrees / step) * step);
}

/**
 * Constrains `point` to a direction from `origin` that is a multiple of
 * `angleStep` degrees — Shift-drawing a wall at 0/15/30/…°. The distance
 * from `origin` stays, or is rounded to `lengthStep` when given.
 */
export function snapToAngle(
  origin: NxPlanPoint,
  point: NxPlanPoint,
  angleStep: number,
  lengthStep = 0
): NxPlanPoint {
  const length = snapValue(pointDistance(origin, point), lengthStep);
  if (length === 0) return { ...origin };
  const angle = snapAngle(segmentAngle(origin, point), angleStep);
  const out = pointAtAngle(origin, angle, length);
  return { x: Number(out.x.toFixed(10)), y: Number(out.y.toFixed(10)) };
}

export interface NxPlanPointSnap {
  point: NxPlanPoint;
  /** Index of the candidate that caught the point. */
  index: number;
  /** World distance from the input point. */
  distance: number;
}

/**
 * The candidate nearest to `point` if it lies within the tolerance, else
 * `null`. Ties go to the earlier candidate.
 */
export function snapToPoint(
  point: NxPlanPoint,
  candidates: readonly NxPlanPoint[],
  tolerance: NxPlanSnapTolerance
): NxPlanPointSnap | null {
  const radius = screenToWorldDistance(tolerance.tolerancePx, tolerance.pixelsPerUnit);
  let best: NxPlanPointSnap | null = null;
  candidates.forEach((candidate, index) => {
    const distance = pointDistance(point, candidate);
    if (distance <= radius && (!best || distance < best.distance)) {
      best = { point: { ...candidate }, index, distance };
    }
  });
  return best;
}

export type NxPlanSegmentTuple = readonly [NxPlanPoint, NxPlanPoint];

export interface NxPlanSegmentSnap extends NxPlanPointSnap {
  /** Position along the segment: 0 = start, 1 = end. */
  t: number;
}

/**
 * Projects `point` onto the nearest of `segments` if that projection lies
 * within the tolerance, else `null` — placing a door onto a wall.
 */
export function snapToSegment(
  point: NxPlanPoint,
  segments: readonly NxPlanSegmentTuple[],
  tolerance: NxPlanSnapTolerance
): NxPlanSegmentSnap | null {
  const radius = screenToWorldDistance(tolerance.tolerancePx, tolerance.pixelsPerUnit);
  let best: NxPlanSegmentSnap | null = null;
  segments.forEach(([a, b], index) => {
    const projection = projectOntoSegment(point, a, b);
    if (projection.distance <= radius && (!best || projection.distance < best.distance)) {
      best = { point: projection.point, index, distance: projection.distance, t: projection.t };
    }
  });
  return best;
}

export type NxPlanSnapKind = 'point' | 'segment' | 'grid' | 'none';

export interface NxPlanSnapOptions extends NxPlanSnapTolerance {
  /** Points to catch (corners, pins); they win over segments and the grid. */
  points?: readonly NxPlanPoint[];
  /** Segments to project onto (walls); they win over the grid. */
  segments?: readonly NxPlanSegmentTuple[];
  /** Grid step in world units; unset or 0 = no grid snapping. */
  grid?: number;
  gridOrigin?: NxPlanPoint;
}

export interface NxPlanSnapResult {
  point: NxPlanPoint;
  kind: NxPlanSnapKind;
  /** Index into `points` or `segments` for those kinds. */
  index?: number;
  /** Position along the segment for kind `segment`. */
  t?: number;
}

/**
 * One snapping pass as an editor needs it: an existing point first, then a
 * segment, then the grid; otherwise the point stays (`kind: 'none'`).
 */
export function snapPoint(point: NxPlanPoint, options: NxPlanSnapOptions): NxPlanSnapResult {
  if (options.points?.length) {
    const hit = snapToPoint(point, options.points, options);
    if (hit) return { point: hit.point, kind: 'point', index: hit.index };
  }
  if (options.segments?.length) {
    const hit = snapToSegment(point, options.segments, options);
    if (hit) return { point: hit.point, kind: 'segment', index: hit.index, t: hit.t };
  }
  if (options.grid && options.grid > 0) {
    return { point: snapToGrid(point, options.grid, options.gridOrigin), kind: 'grid' };
  }
  return { point: { ...point }, kind: 'none' };
}
